import { useEffect, useState, useRef } from 'react';
import { Mic, Square, X, Send, Play, Pause, Trash2, Loader2 } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { Button } from '@caruso/ui';
import { cn } from '@caruso/ui';

interface AudioRecorderProps {
  onCancel: () => void;
  onSend: (audioBlob: Blob) => void;
  isUploading?: boolean;
}

export function AudioRecorder({ onCancel, onSend, isUploading }: AudioRecorderProps) {
  const {
    isRecording,
    recordingTime,
    waveformData,
    audioBlob,
    audioUrl,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    resetRecording,
  } = useAudioRecorder();

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Start recording on mount
  useEffect(() => {
    startRecording();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (!audioUrl || !audioRef.current) {
      audioRef.current = new Audio(audioUrl!);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setPlaybackTime(0);
      };
      audioRef.current.ontimeupdate = () => {
        setPlaybackTime(Math.floor(audioRef.current!.currentTime));
      };
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSend = () => {
    if (audioBlob) {
      onSend(audioBlob);
    }
  };

  const handleCancel = () => {
    cancelRecording();
    onCancel();
  };

  const handleReset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setPlaybackTime(0);
    resetRecording();
    startRecording();
  };

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
        <p className="text-sm text-red-400">{error}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="text-red-400"
        >
          Fechar
        </Button>
      </div>
    );
  }

  // Recording state
  if (isRecording && !audioBlob) {
    return (
      <div className="flex items-center gap-3">
        {/* Cancel Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          className="text-red-400 hover:bg-red-400/10"
        >
          <X className="w-5 h-5" />
        </Button>

        {/* Waveform */}
        <div className="flex-1 flex items-center gap-0.5 h-10 bg-brand-smoke/50 rounded-lg px-3 overflow-hidden">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse mr-2" />
          {waveformData.map((amplitude, index) => (
            <div
              key={index}
              className="w-1 bg-brand-gold rounded-full transition-all"
              style={{
                height: `${Math.max(4, amplitude * 40)}px`,
              }}
            />
          ))}
          {/* Fill remaining space with placeholder bars */}
          {Array.from({ length: Math.max(0, 50 - waveformData.length) }).map((_, index) => (
            <div
              key={`placeholder-${index}`}
              className="w-1 h-1 bg-brand-gold/20 rounded-full"
            />
          ))}
        </div>

        {/* Timer */}
        <span className="text-sm text-brand-ivory font-mono min-w-[50px]">
          {formatTime(recordingTime)}
        </span>

        {/* Stop Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={stopRecording}
          className="text-brand-gold hover:bg-brand-gold/10"
        >
          <Square className="w-5 h-5 fill-current" />
        </Button>
      </div>
    );
  }

  // Preview state (after recording)
  if (audioBlob && audioUrl) {
    return (
      <div className="flex items-center gap-3">
        {/* Delete Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleReset}
          disabled={isUploading}
          className="text-red-400 hover:bg-red-400/10"
        >
          <Trash2 className="w-5 h-5" />
        </Button>

        {/* Play/Pause Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePlayPause}
          disabled={isUploading}
          className="text-brand-gold hover:bg-brand-gold/10"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </Button>

        {/* Waveform (static preview) */}
        <div className="flex-1 flex items-center gap-0.5 h-10 bg-brand-smoke/50 rounded-lg px-3 overflow-hidden">
          {waveformData.map((amplitude, index) => {
            const progress = (playbackTime / recordingTime) * waveformData.length;
            const isPlayed = index < progress;

            return (
              <div
                key={index}
                className={cn(
                  'w-1 rounded-full transition-colors',
                  isPlayed ? 'bg-brand-gold' : 'bg-brand-gold/30'
                )}
                style={{
                  height: `${Math.max(4, amplitude * 40)}px`,
                }}
              />
            );
          })}
        </div>

        {/* Duration */}
        <span className="text-sm text-brand-ivory font-mono min-w-[50px]">
          {formatTime(isPlaying ? playbackTime : recordingTime)}
        </span>

        {/* Send Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSend}
          disabled={isUploading}
          className="text-brand-gold hover:bg-brand-gold/10"
        >
          {isUploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </div>
    );
  }

  // Initial state (should not normally be visible)
  return (
    <div className="flex items-center justify-center p-4">
      <Loader2 className="w-6 h-6 text-brand-gold animate-spin" />
    </div>
  );
}
